import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from "@mui/material";
import { apiService } from "../../api/services/apiService";
import PmgAssignmentSection from "../product-management-groups/PmgAssignmentSection";
import type { FAQ, EntityStatus } from "../../types";
import { EntityType } from "../../types";

interface EditFaqDialogProps {
  faq: FAQ | null;
  open: boolean;
  onClose: () => void;
  onSaved: (updated: FAQ) => void;
}

const EditFaqDialog: React.FC<EditFaqDialogProps> = ({
  faq,
  open,
  onClose,
  onSaved,
}) => {
  const [draft, setDraft] = useState<FAQ | null>(faq);
  const [saving, setSaving] = useState(false);
  const [pmgAccessDenied, setPmgAccessDenied] = useState(false);
  const readOnly = pmgAccessDenied;

  useEffect(() => {
    setDraft(faq);
  }, [faq]);

  const handleSave = async () => {
    if (!draft?.uuid) return;
    setSaving(true);
    try {
      const updated = await apiService.updateFaq(draft.uuid, {
        name: draft.name,
        description: draft.description ?? undefined,
        question: draft.question ?? undefined,
        answer: draft.answer ?? undefined,
        status: draft.status,
      });
      onSaved(updated);
      onClose();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : "Failed to update FAQ");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit FAQ</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <TextField
          fullWidth
          label="Name"
          margin="normal"
          value={draft?.name ?? ""}
          disabled={readOnly}
          onChange={(e) =>
            setDraft((prev) => (prev ? { ...prev, name: e.target.value } : prev))
          }
        />
        <TextField
          fullWidth
          label="Description"
          margin="normal"
          value={draft?.description ?? ""}
          disabled={readOnly}
          onChange={(e) =>
            setDraft((prev) =>
              prev ? { ...prev, description: e.target.value } : prev,
            )
          }
        />
        <TextField
          select
          fullWidth
          label="Status"
          margin="normal"
          value={draft?.status ?? "INACTIVE"}
          disabled={readOnly}
          onChange={(e) =>
            setDraft((prev) =>
              prev ? { ...prev, status: e.target.value as EntityStatus } : prev,
            )
          }
        >
          <MenuItem value="ACTIVE">ACTIVE</MenuItem>
          <MenuItem value="INACTIVE">INACTIVE</MenuItem>
        </TextField>
        <TextField
          fullWidth
          label="Question"
          margin="normal"
          value={draft?.question ?? ""}
          disabled={readOnly}
          onChange={(e) =>
            setDraft((prev) =>
              prev ? { ...prev, question: e.target.value } : prev,
            )
          }
        />
        <TextField
          fullWidth
          multiline
          minRows={3}
          label="Answer"
          margin="normal"
          value={draft?.answer ?? ""}
          disabled={readOnly}
          onChange={(e) =>
            setDraft((prev) =>
              prev ? { ...prev, answer: e.target.value } : prev,
            )
          }
        />
        {draft?.uuid && (
          <PmgAssignmentSection
            entityUuid={draft.uuid}
            entityType={EntityType.FAQ}
            onAccessDenied={setPmgAccessDenied}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || pmgAccessDenied}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditFaqDialog;
